/*
  Datumshelfer für das Monatsraster.

  Zeitraum-Namen und Wochengrenzen kommen aus dem Design System
  (utils/periodDates): dieselbe Definition, die auch die Zeitraum-Navigation
  benutzt – sonst hiesse dieselbe Woche hier anders als in der Navigation.
  Hier stehen nur die Helfer, die das Raster zusätzlich braucht.
*/

import {
  getWeekBounds,
  parseDateValue,
  toIsoDate,
} from "../../../../design-system/utils/periodDates";

export {
  formatMonthTitle,
  formatWeekRange,
  formatWeekTitle,
  getIsoWeekNumber,
  getMonthPickerOptions,
  getWeekBounds,
  getWeekPickerOptions,
  isSameMonth,
  isSameWeek,
  parseDateValue,
  toIsoDate,
} from "../../../../design-system/utils/periodDates";

const WEEKDAY_SHORT_FORMATTER = new Intl.DateTimeFormat("de-CH", { weekday: "short" });
const WEEKDAY_LONG_FORMATTER = new Intl.DateTimeFormat("de-CH", { weekday: "long" });

function formatDayMonthLabel(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

export function addMonthsToDate(value, amount) {
  const base = parseDateValue(value) || new Date();
  return new Date(base.getFullYear(), base.getMonth() + amount, 1);
}

export function addWeeksToDate(value, amount) {
  const base = parseDateValue(value) || new Date();
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + amount * 7);
}

export function getMonthBounds(value) {
  const base = parseDateValue(value) || new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return { start, end };
}

/* Ein Tag des Rasters: Nummer, Wochentag, Wochenende, heute. */
function buildDaysBetween(start, end) {
  const todayKey = toIsoDate(new Date());
  const days = [];

  for (
    let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    cursor <= end;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  ) {
    const key = toIsoDate(cursor);
    days.push({
      key,
      date: cursor,
      dayNumber: cursor.getDate(),
      dayMonthLabel: formatDayMonthLabel(cursor),
      weekdayShort: WEEKDAY_SHORT_FORMATTER.format(cursor),
      weekdayLong: WEEKDAY_LONG_FORMATTER.format(cursor),
      isWeekend: cursor.getDay() === 0 || cursor.getDay() === 6,
      isToday: key === todayKey,
    });
  }

  return days;
}

export function getMonthDays(value) {
  const { start, end } = getMonthBounds(value);
  return buildDaysBetween(start, end);
}

export function getDaysInRange(startValue, endValue) {
  const start = parseDateValue(startValue);
  const end = parseDateValue(endValue);
  if (!start || !end || end < start) return [];
  return buildDaysBetween(start, end);
}

export function getWeekDays(value) {
  const { start, end } = getWeekBounds(value);
  return buildDaysBetween(start, end);
}

export function formatTime(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("de-CH", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
